/* ---------------------------------------------
   BACKEND CUSTOM FILTER SYSTEM
--------------------------------------------- */

console.log("🔧 Backend Filter System Loading...");

let currentPage = 1;
const ITEMS_PER_PAGE = 24;

const FILTER_API_URL = "/apps/api/filter/query";

window.currentCollectionState = window.currentCollectionState || {
  filteredResults: [],
  collectionHandle: null,
  facets: {},
};

function normalizeValue(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[-_\s]+/g, " ")
    .trim();
}

function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getCollectionHandle() {
  if (window.meta?.collection?.handle) return window.meta.collection.handle;

  const pathMatch = window.location.pathname.match(/\/collections\/([^/]+)/);
  if (pathMatch?.[1] && pathMatch[1] !== "all") return pathMatch[1];

  return "all";
}

function getFilterGroupFromInput(input) {
  const name = input.name.toLowerCase();

  if (name.includes("vendor") || name.includes("brand")) return "vendor";

  if (name.includes("filter.v.m.custom.variant_category")) {
    return "variantCategory";
  }

  if (name.includes("filter.p.m.custom.product_casting")) {
    return "casting";
  }

  if (
    name.includes("filter.p.product_type") ||
    name.includes("filter.p.tag") ||
    name.includes("filter.p.m.custom.category")
  ) {
    return "category";
  }

  return "casting";
}

function getActiveFiltersFromDOM() {
  const filters = {
    vendors: [],
    categories: [],
    productCastings: [],
    variantCategories: [],
    price: {
      min: 0,
      max: Infinity,
    },
    sortBy: "manual",
  };

  document
    .querySelectorAll(
      '.facets input[type="checkbox"]:checked, .mobile-facets__checkbox:checked',
    )
    .forEach((input) => {
      const group = getFilterGroupFromInput(input);
      const value = input.value;

      if (group === "vendor") filters.vendors.push(value);
      else if (group === "category") filters.categories.push(value);
      else if (group === "variantCategory") filters.variantCategories.push(value);
      else filters.productCastings.push(value);
    });

  filters.vendors = [...new Set(filters.vendors)];
  filters.categories = [...new Set(filters.categories)];
  filters.productCastings = [...new Set(filters.productCastings)];
  filters.variantCategories = [...new Set(filters.variantCategories)];

  const minInput = document.querySelector(
    'price-range input[name*="min"], price-range input[name*="gte"]',
  );
  const maxInput = document.querySelector(
    'price-range input[name*="max"], price-range input[name*="lte"]',
  );

  if (minInput?.value) filters.price.min = parseFloat(minInput.value);
  if (maxInput?.value) filters.price.max = parseFloat(maxInput.value);

  const desktopSort = document.querySelector('select[name="sort_by"]');
  const mobileSort = document.querySelector("#SortBy-mobile");

  filters.sortBy = desktopSort?.value || mobileSort?.value || "manual";

  return filters;
}

function buildApiUrl(filters, page) {
  const params = new URLSearchParams();

  params.set("shop", Shopify.shop);
  params.set("collection_handle", window.currentCollectionState.collectionHandle || "all");
  params.set("page", page);
  params.set("limit", ITEMS_PER_PAGE);
  params.set("sort_by", filters.sortBy || "manual");

  if (filters.vendors.length) {
    params.set("vendor", filters.vendors.join(","));
  }

  if (filters.categories.length) {
    params.set("category", filters.categories.join(","));
  }

  if (filters.productCastings.length) {
    params.set("casting", filters.productCastings.join(","));
  }

  if (filters.variantCategories.length) {
    params.set("variant_category", filters.variantCategories.join(","));
  }

  if (filters.price.min > 0) params.set("price_min", filters.price.min);
  if (filters.price.max < Infinity) params.set("price_max", filters.price.max);

  return `${FILTER_API_URL}?${params.toString()}`;
}

async function fetchFilteredProducts(filters, page) {
  const url = buildApiUrl(filters, page);

  const res = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
  });

  if (!res.ok) {
    throw new Error(`Filter API failed: ${res.status}`);
  }

  return await res.json();
}

function updateURLWithFilters(filters, page) {
  const params = new URLSearchParams();

  filters.vendors.forEach((value) => params.append("vendor", value));
  filters.categories.forEach((value) => params.append("category", value));
  filters.productCastings.forEach((value) => params.append("casting", value));
  filters.variantCategories.forEach((value) =>
    params.append("variant_category", value),
  );

  if (filters.price.min > 0) params.set("price_min", filters.price.min);
  if (filters.price.max < Infinity) params.set("price_max", filters.price.max);
  if (filters.sortBy !== "manual") params.set("sort_by", filters.sortBy);
  if (page > 1) params.set("page", page);

  const queryString = params.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  history.replaceState({}, "", newUrl);
}

function applyFiltersFromURL() {
  const params = new URLSearchParams(window.location.search);

  document
    .querySelectorAll('.facets input[type="checkbox"], .mobile-facets__checkbox')
    .forEach((checkbox) => {
      checkbox.checked = false;
    });

  const vendors = params.getAll("vendor");
  const categories = params.getAll("category");
  const castings = params.getAll("casting");
  const variantCategories = params.getAll("variant_category");

  document
    .querySelectorAll('.facets input[type="checkbox"], .mobile-facets__checkbox')
    .forEach((input) => {
      const value = decodeURIComponent(input.value);

      if (
        vendors.includes(value) ||
        categories.includes(value) ||
        castings.includes(value) ||
        variantCategories.includes(value)
      ) {
        input.checked = true;
      }
    });

  const minInput = document.querySelector(
    'price-range input[name*="min"], price-range input[name*="gte"]',
  );
  const maxInput = document.querySelector(
    'price-range input[name*="max"], price-range input[name*="lte"]',
  );

  if (minInput && params.has("price_min")) minInput.value = params.get("price_min");
  if (maxInput && params.has("price_max")) maxInput.value = params.get("price_max");

  const sortValue = params.get("sort_by");

  if (sortValue) {
    document.querySelectorAll('select[name="sort_by"], #SortBy-mobile').forEach((select) => {
      select.value = sortValue;
    });
  }

  currentPage = parseInt(params.get("page"), 10) || 1;
}

async function runFilterLogic() {
  console.log("🔄 Running backend filter logic...");

  const filters = getActiveFiltersFromDOM();

  renderSkeletonGrid(9);

  try {
    const data = await fetchFilteredProducts(filters, currentPage);

    window.currentCollectionState.filteredResults = data.products || [];
    window.currentCollectionState.facets = data.facets || {};

    renderProductsToShopifyGrid(data.products || [], data);
    updateShopifyProductCount(data.total || 0);
    updateActiveFilterBadges(filters);
    updateAvailableFacetOptions(data.facets || {});
    updateURLWithFilters(filters, currentPage);

    console.log("✅ Backend filter complete", data);
  } catch (error) {
    console.error("❌ Backend filter failed:", error);
  }
}

/* ---------------------------------------------
   SKELETON LOADING
--------------------------------------------- */

(function injectSkeletonStyles() {
  if (document.getElementById("skeleton-filter-styles")) return;

  const style = document.createElement("style");
  style.id = "skeleton-filter-styles";
  style.textContent = `
    @keyframes skeleton-shimmer {
      0% { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }

    .skeleton-card .card__inner {
      background: #ececec !important;
      --ratio-percent: 0% !important;
      padding-top: 0 !important;
      height: 160px !important;
      min-height: unset !important;
    }

    .skeleton-shimmer {
      background: linear-gradient(
        90deg,
        #ececec 25%,
        #d8d8d8 50%,
        #ececec 75%
      );
      background-size: 600px 100%;
      animation: skeleton-shimmer 1.4s ease-in-out infinite;
    }

    .skeleton-line {
      border-radius: 4px;
    }
  `;

  document.head.appendChild(style);
})();

function renderSkeletonGrid(count = 8) {
  const grid = document.querySelector(
    "#product-grid, #ProductGridContainer ul, .collection .grid",
  );

  if (!grid) return;

  let html = "";

  for (let i = 0; i < count; i++) {
    html += `
      <li class="grid__item skeleton-card">
        <div class="card-wrapper product-card-wrapper">
          <div class="card card--standard card--media">
            <div class="card__inner color-scheme-2 gradient ratio">
              <div class="card__media skeleton-shimmer"></div>
            </div>
            <div class="card__content custom_card__content" style="padding: 10px 0;">
              <div class="card__information custom_card__information">
                <div class="skeleton-line skeleton-shimmer" style="height: 13px; margin-bottom: 7px;"></div>
                <div class="skeleton-line skeleton-shimmer" style="height: 11px; width: 50%;"></div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  grid.innerHTML = html;
}

/* ---------------------------------------------
   PRODUCT RENDERING
--------------------------------------------- */

function formatPrice(raw) {
  const amount = Number.parseFloat(String(raw || "0").replace(/[^\d.]/g, "")) || 0;
  const currency = String(raw || "").match(/[A-Z]{3}/)?.[0] || "AUD";

  const formatted = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
  }).format(amount);

  return `${formatted} ${currency}`;
}

function generateSrcset(imageUrl) {
  if (!imageUrl) return "";

  const baseUrl = imageUrl.split("?")[0];
  const widths = [165, 360, 533, 720, 940, 1000];

  return widths
    .map((width) => `${baseUrl}?width=${width} ${width}w`)
    .join(",");
}

function cartIconSvg() {
  return `
    <svg class="custom__icon_1" xmlns="http://www.w3.org/2000/svg" id="Group_84449" data-name="Group 84449" width="38" height="38" viewBox="0 0 38 38">
      <circle id="Ellipse_105" data-name="Ellipse 105" cx="19" cy="19" r="19" fill="#f4f4f4"></circle>
      <g id="Icon_feather-shopping-cart" data-name="Icon feather-shopping-cart" transform="translate(8.453 8.5)">
        <path id="Path_83205" data-name="Path 83205" d="M13.918,30.959A.959.959,0,1,1,12.959,30,.959.959,0,0,1,13.918,30.959Z" transform="translate(-5.288 -11.783)" fill="none" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
        <path id="Path_83206" data-name="Path 83206" d="M30.418,30.959A.959.959,0,1,1,29.459,30,.959.959,0,0,1,30.418,30.959Z" transform="translate(-11.241 -11.783)" fill="none" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
        <path id="Path_83207" data-name="Path 83207" d="M1.5,1.5H5.335L7.9,14.338a1.918,1.918,0,0,0,1.918,1.544h9.32a1.918,1.918,0,0,0,1.918-1.544l1.534-8.044H6.294" transform="translate(-1.5 -1.5)" fill="none" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
      </g>
    </svg>`;
}

function renderProductsToShopifyGrid(products, data) {
  const grid = document.querySelector(
    "#product-grid, #ProductGridContainer ul, .collection .grid",
  );

  if (!grid) return;

  if (!products.length) {
    grid.innerHTML = `
      <li class="grid__item">
        <div class="collection__empty">
          <p>No products found matching your filters.</p>
        </div>
      </li>
    `;

    renderShopifyPagination(data);
    return;
  }

  grid.innerHTML = products
    .map((product, index) => generateShopifyProductCard(product, index))
    .join("");

  renderShopifyPagination(data);
}

function generateShopifyProductCard(product, index) {
  const uniqueId = `product-${String(product.id || "").replace(/\D/g, "")}-${index}`;
  const productId = String(product.id || "").replace(/\D/g, "");

  // Use properties directly from our backend serializer payload
  const variant = product.selectedVariant || {};
  const variantId = product.selectedVariantId || "";

  const image = product.image || "https://via.placeholder.com/533x533?text=No+Image";
  
  // Link directly to the selected variant
  const productUrl = product.url 
    ? product.url 
    : `/products/${product.handle}${variantId ? `?variant=${variantId}` : ""}`;

  const ratioPercent = product.isSingleProduct ? "37%" : "141.39459895203547%";
const castingTitle = (product.castingTitle || "")
  .replace(/^(light|dark)\s*\/\s*/i, "")
  .trim();
    const priceValue = Number(product.priceValue || 0);

  // Derive availability strictly from serializer booleans
  const variantAvailable = product.available && variant.available !== false;
  const variantCount = Number(product.variantCount || 1);

  return `
<li class="grid__item scroll-trigger animate--slide-in" data-cascade="" style="--animation-order: ${index + 1};">
  <div class="card-wrapper product-card-wrapper underline-links-hover collection-item wishlist-item" data-product-id="${productId}">
    <div class="card card--standard card--media" style="--ratio-percent: ${ratioPercent};">
      <div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent: ${ratioPercent};">
        <div class="card__media">
          <div class="media media--transparent media--hover-effect">
            <img
              srcset="${generateSrcset(image)}"
              src="${image}"
              sizes="(min-width: 1400px) 423px, (min-width: 990px) calc((100vw - 130px) / 3), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
              alt="${product.imageAlt || product.title || ""}"
              class="motion-reduce"
              loading="lazy"
              width="1000"
              height="1000"
              onerror="this.src='https://via.placeholder.com/533x533?text=No+Image'"
            >
          </div>
        </div>

        <div class="card__badge bottom left">
          ${!variantAvailable ? '<span class="badge badge--bottom-left">Sold out</span>' : ""}
        </div>
      </div>

      <div class="single-product-card card__content custom_card__content">
        <div class="card__information custom_card__information">
          <h3 class="card__heading h5" id="title-${uniqueId}">
            <a href="${productUrl}" id="CardLink-${uniqueId}" class="full-unstyled-link">
              ${product.title || ""}
            </a>

            ${
              castingTitle
                ? `
                  <a href="${productUrl}" class="product-card-casting full-unstyled-link">
                    ${castingTitle}
                  </a>
                `
                : ""
            }
          </h3>
        </div>

        <div class="custom__cart__icon">
          <div class="card-information">
            <div class="price single-price" id="price-${uniqueId}">
              <div class="price__container">
                <div class="price__regular">
                  <span class="visually-hidden visually-hidden--inline">Regular price</span>
                  <span class="price-item price-item--regular dynamic-price">
                    ${formatPrice(priceValue)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            class="custom__cart__icons custom__cart__icon_wishlist"
            data-wishlist-id="${variantId}"
            data-product-handle="${(product.handle || "")}"
            role="button"
            tabindex="0"
            aria-label="Add to Wishlist"
            aria-pressed="false"
            title="Add to Wishlist"
            data-state="inactive"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38">
              <circle cx="19" cy="19" r="19" fill="#f4f4f4"></circle>
              <path id="heart-shape" d="M20.624,6.026a5.218,5.218,0,0,0-7.381,0L12.238,7.032,11.232,6.026a5.219,5.219,0,1,0-7.381,7.381l1.006,1.006,7.381,7.381,7.381-7.381,1.006-1.006a5.218,5.218,0,0,0,0-7.381Z" transform="translate(6.856 6.043)" fill="none" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
            </svg>
          </div>

          <div class="quick-add no-js-hidden">
            <product-form data-section-id="">
              <form method="post" action="/cart/add" id="quick-add-${uniqueId}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
                <input type="hidden" name="form_type" value="product">
                <input type="hidden" name="utf8" value="✓">
                <input type="hidden" name="id" value="${variantId}" class="product-variant-id">
                <input type="hidden" name="product-id" value="${productId}">
                <input type="hidden" name="section-id" value="">

                ${
                  !variantAvailable
                    ? `
                      <div
                        class="custom__cart__icons custom__cart__icon_notify-me"
                        id="notifyBtn"
                        data-product-id="${productId}"
                        data-variant-id="${variantId}"
                        data-product-title="${product.title || ""}"
                        data-variant-title="${variant.title || "Default Title"}"
                        data-product-image="${image}"
                      >
                        ${cartIconSvg()}
                      </div>
                    `
                    : variantCount > 1
                      ? `
                        <div class="custom__cart__icons custom-quick-add-btn" data-product-handle="${product.handle || ""}">
                          ${cartIconSvg()}
                        </div>
                      `
                      : `
                        <button id="quick-add-${uniqueId}-submit" type="submit" name="add" class="quick-add__submit button button--full-width button--secondary">
                          <div class="custom__cart__icons custom__cart__icon__">
                            ${cartIconSvg()}
                          </div>

                          <div class="loading__spinner hidden">
                            <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                              <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                            </svg>
                          </div>
                        </button>
                      `
                }
              </form>
            </product-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</li>`;
}

/* ---------------------------------------------
   FACET AVAILABILITY
--------------------------------------------- */

function getFacetCountForInput(input, facets) {
  const group = getFilterGroupFromInput(input);
  const key = normalizeValue(input.value);

  if (group === "vendor") {
    return facets.vendors?.[key] || facets.vendors?.[input.value] || 0;
  }

  if (group === "category") {
    return facets.categories?.[key] || facets.categories?.[input.value] || 0;
  }

  if (group === "casting") {
    return facets.productCastings?.[key] || facets.productCastings?.[input.value] || 0;
  }

  if (group === "variantCategory") {
    return facets.variantCategories?.[key] || facets.variantCategories?.[input.value] || 0;
  }

  return 0;
}

function updateAvailableFacetOptions(facets) {
  document
    .querySelectorAll('.facets input[type="checkbox"], .mobile-facets__checkbox')
    .forEach((input) => {
      if (input.checked) {
        showFacetOption(input);
        return;
      }

      const count = getFacetCountForInput(input, facets);

      if (count > 0) {
        showFacetOption(input);
      } else {
        hideFacetOption(input);
      }
    });

  sortFacetOptions();
}

function hideFacetOption(input) {
  const item =
    input.closest("li") ||
    input.closest(".facets__item") ||
    input.closest(".mobile-facets__item");

  if (!item) return;

  item.style.opacity = "0.4";
  item.style.pointerEvents = "none";
  input.disabled = true;
}

function showFacetOption(input) {
  const item =
    input.closest("li") ||
    input.closest(".facets__item") ||
    input.closest(".mobile-facets__item");

  if (!item) return;

  item.style.opacity = "";
  item.style.pointerEvents = "";
  input.disabled = false;
}

function sortFacetOptions() {
  document
    .querySelectorAll(
      ".facets__list, .facets__display ul, .facets__display, .mobile-facets__list",
    )
    .forEach((container) => {
      const items = [...container.children].filter((item) =>
        item.querySelector('input[type="checkbox"]'),
      );

      if (!items.length) return;

      items.sort((a, b) => {
        const aInput = a.querySelector('input[type="checkbox"]');
        const bInput = b.querySelector('input[type="checkbox"]');

        if (aInput.checked && !bInput.checked) return -1;
        if (!aInput.checked && bInput.checked) return 1;

        if (!aInput.disabled && bInput.disabled) return -1;
        if (aInput.disabled && !bInput.disabled) return 1;

        return a.textContent.trim().localeCompare(b.textContent.trim());
      });

      items.forEach((item) => container.appendChild(item));
    });
}

/* ---------------------------------------------
   ACTIVE FILTER BADGES
--------------------------------------------- */

function updateActiveFilterBadges(filters) {
  const containers = document.querySelectorAll(
    ".active-facets-desktop, .active-facets-mobile",
  );

  containers.forEach((container) => {
    container.querySelectorAll(".active-facets__button").forEach((badge) => {
      if (!badge.classList.contains("active-facets__button-remove")) {
        badge.remove();
      }
    });

    const clearAllButton = container.querySelector(".active-facets__button-remove");

    filters.vendors.forEach((value) =>
      addFilterBadge(container, "Vendor", value, clearAllButton),
    );

    filters.categories.forEach((value) =>
      addFilterBadge(container, "Category", value, clearAllButton),
    );

    filters.productCastings.forEach((value) =>
      addFilterBadge(container, "Casting", value, clearAllButton),
    );

    filters.variantCategories.forEach((value) =>
      addFilterBadge(container, "Casting Category", value, clearAllButton),
    );

    if (filters.price?.min > 0 || filters.price?.max < Infinity) {
      addFilterBadge(
        container,
        "Price",
        `${filters.price.min} - ${
          filters.price.max === Infinity ? "∞" : filters.price.max
        }`,
        clearAllButton,
      );
    }

    const hasActiveFilters =
      filters.vendors.length ||
      filters.categories.length ||
      filters.productCastings.length ||
      filters.variantCategories.length ||
      filters.price?.min > 0 ||
      filters.price?.max < Infinity;

    if (clearAllButton) {
      clearAllButton.style.display = hasActiveFilters ? "" : "none";
    }
  });

  updateFacetCounts();
}

function addFilterBadge(container, label, value, insertBefore) {
  const badge = document.createElement("div");

  badge.className = "active-facets__button active-facets__button--light";

  badge.innerHTML = `
    <span class="active-facets__button-inner button button--tertiary">
      ${label}: ${value}
      <span class="svg-wrapper">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close-small" viewBox="0 0 12 13">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"></path>
        </svg>
      </span>
    </span>
  `;

  if (insertBefore && insertBefore.parentElement === container) {
    container.insertBefore(badge, insertBefore);
  } else {
    container.appendChild(badge);
  }

  badge.addEventListener("click", (event) => {
    event.preventDefault();

    removeFilterFromButton(badge);
    currentPage = 1;
    runFilterLogic();
  });
}

function removeFilterFromButton(button) {
  const buttonText = button.textContent.trim();
  const parts = buttonText.split(":");

  if (parts.length >= 2) {
    const filterValue = parts[1].trim();

    document
      .querySelectorAll(
        '.facets input[type="checkbox"]:checked, .mobile-facets__checkbox:checked',
      )
      .forEach((checkbox) => {
        if (checkbox.value === filterValue || buttonText.includes(checkbox.value)) {
          checkbox.checked = false;
        }
      });
  }

  if (buttonText.includes("$") || buttonText.match(/\d+\s*-\s*\d+/)) {
    document.querySelectorAll('price-range input[type="number"]').forEach((input) => {
      input.value = "";
    });
  }
}

function updateFacetCounts() {
  document.querySelectorAll(".facets__disclosure").forEach((details) => {
    const summaryLabel = details.querySelector(".facets__summary-label");
    const selectedSpan = details.querySelector(".facets__selected");

    if (!summaryLabel || !selectedSpan) return;

    const checkedInputs = details.querySelectorAll('input[type="checkbox"]:checked');
    const count = checkedInputs.length;

    selectedSpan.textContent = `${count} selected`;

    const summary = details.querySelector(".facets__summary");

    if (summary) {
      const labelText = summaryLabel.textContent.trim();
      summary.setAttribute("aria-label", `${labelText} (${count} selected)`);
    }
  });
}

/* ---------------------------------------------
   PAGINATION
--------------------------------------------- */

function renderShopifyPagination(data) {
  const totalPages = data.totalPages || 0;
  const page = data.page || 1;

  let pagination = document.querySelector(".pagination-wrapper, nav[role='navigation']");

  if (!pagination) {
    pagination = document.createElement("div");
    pagination.className = "pagination-wrapper";

    const grid = document.querySelector("#product-grid, #ProductGridContainer ul");
    grid?.parentElement?.appendChild(pagination);
  }

  if (totalPages <= 1) {
    pagination.innerHTML = "";
    return;
  }

  let html = '<nav class="pagination" role="navigation"><ul class="pagination__list list-unstyled">';

  const MAX_VISIBLE_PAGES = 5;

  let start = Math.max(1, page - Math.floor(MAX_VISIBLE_PAGES / 2));
  let end = Math.min(totalPages, start + MAX_VISIBLE_PAGES - 1);

  if (end - start + 1 < MAX_VISIBLE_PAGES) {
    start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);
  }

  if (page > 1) {
    html += `
      <li>
        <a href="#" class="pagination__item pagination__item--prev link" data-page="${page - 1}">
           <svg class="icon icon-caret" viewBox="0 0 10 6">
                            <path fill="currentColor" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708"></path>
                        </svg>
        </a>
      </li>
    `;
  }

  if (start > 1) {
    html += `<li><a href="#" class="pagination__item link" data-page="1">1</a></li>`;
    if (start > 2) html += `<li><span class="pagination__item">...</span></li>`;
  }

  for (let i = start; i <= end; i++) {
    html +=
      i === page
        ? `<li><span class="pagination__item pagination__item--current">${i}</span></li>`
        : `<li><a href="#" class="pagination__item link" data-page="${i}">${i}</a></li>`;
  }

  if (end < totalPages) {
    if (end < totalPages - 1) html += `<li><span class="pagination__item">...</span></li>`;
    html += `<li><a href="#" class="pagination__item link" data-page="${totalPages}">${totalPages}</a></li>`;
  }

  if (page < totalPages) {
    html += `
      <li>
        <a href="#" class="pagination__item pagination__item--next link" data-page="${page + 1}">
              <svg class="icon icon-caret" viewBox="0 0 10 6">
                            <path fill="currentColor" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708"></path>
                        </svg>
        </a>
      </li>
    `;
  }

  html += "</ul></nav>";
  pagination.innerHTML = html;

  pagination.querySelectorAll("a[data-page]").forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();

      currentPage = parseInt(link.dataset.page, 10);
      runFilterLogic();

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  });
}

function updateShopifyProductCount(count) {
  const countElements = document.querySelectorAll(
    "#ProductCount, #ProductCountDesktop, .product-count__text",
  );

  const countText = count === 1 ? `${count} product` : `${count} products`;

  countElements.forEach((element) => {
    element.textContent = countText;
  });
}

/* ---------------------------------------------
   CLEAR + LISTENERS + INIT
--------------------------------------------- */

function clearAllFilters() {
  document
    .querySelectorAll(
      '.facets input[type="checkbox"]:checked, .mobile-facets__checkbox:checked',
    )
    .forEach((input) => {
      input.checked = false;
    });

  document.querySelectorAll('price-range input[type="number"]').forEach((input) => {
    input.value = "";
  });

  document.querySelectorAll('select[name="sort_by"], #SortBy-mobile').forEach((select) => {
    select.value = "manual";
  });

  currentPage = 1;
  runFilterLogic();
}

function setupFilterListeners() {
  document
    .querySelectorAll("facet-filters-form form, .facets form, .mobile-facets")
    .forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
    });

  document
    .querySelectorAll('.facets input[type="checkbox"], .mobile-facets__checkbox')
    .forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        currentPage = 1;
        runFilterLogic();
      });
    });

  document.querySelectorAll('price-range input[type="number"]').forEach((input) => {
    input.addEventListener(
      "input",
      debounce(() => {
        currentPage = 1;
        runFilterLogic();
      }, 400),
    );
  });

  document
    .querySelectorAll(".active-facets__button-remove, .mobile-facets__clear")
    .forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        clearAllFilters();
      });
    });

  document.querySelectorAll('select[name="sort_by"], #SortBy-mobile').forEach((select) => {
    select.addEventListener("change", () => {
      currentPage = 1;

      const otherSelect =
        select.id === "SortBy-mobile"
          ? document.querySelector('select[name="sort_by"]')
          : document.querySelector("#SortBy-mobile");

      if (otherSelect && otherSelect.value !== select.value) {
        otherSelect.value = select.value;
      }

      runFilterLogic();
    });
  });

  window.addEventListener("popstate", () => {
    applyFiltersFromURL();
    runFilterLogic();
  });
}

document.addEventListener("DOMContentLoaded", () => {
  window.currentCollectionState.collectionHandle = getCollectionHandle();

  applyFiltersFromURL();
  setupFilterListeners();
  runFilterLogic();

  console.log("✅ Backend Filter System Ready");
});

window.customFilters = {
  getFilters: getActiveFiltersFromDOM,
  rerun: runFilterLogic,
  clear: clearAllFilters,
};